/**
 * OfficerAssignPopover
 *
 * Appears after the user drag-selects (or presses and holds) a time range on a counter row (counter
 * timetable), or taps a single empty cell there. The pivot of CounterAssignPopover: here the row is the
 * counter, so the pick is which officer works it.
 *
 * One step, whatever the range held: put an officer on the counter for the range — on a range someone holds
 * it opens on them, so a change of officer is one pick — or clear it.
 *
 * Start, end and the officer are all editable before anything is saved: a finger-drawn selection is often a
 * slot out, so the two time pickers are the correction, and nothing is written until Save is pressed.
 */

import { useEffect, useRef, useState } from "react";
import { TimeRangeSelect } from "@/components/TimeRangeSelect";
import { PopoverShell } from "@/components/PopoverShell";
import { ActionNote, ActionTabs, PopoverFooter, PopoverHeader } from "@/components/PopoverParts";
import { fmtHhmm, isNarrow } from "@/utils/popoverLayout";

const linkBtn = (color: string) => ({ background: "none", border: "none", color, cursor: "pointer", fontSize: 12, padding: 0, textAlign: "left" as const });
import { CELL_ACTION_LOOK, type CellAction, type CellActionKind } from "@/utils/cellAction";

/** The select's value for "someone not on the roster yet". */
export const NEW_OFFICER_ID = "__new__";

export interface OfficerOption {
  id: string;
  label: string;
}

interface OfficerAssignPopoverProps {
  startTime: string;
  endTime: string;
  /** Shift the grid is showing — drives the time pickers' option list. */
  shiftType?: string;
  /**
   * Called when the user edits the range. The parent owns start/end (it also
   * repaints the grid highlight from them), so this component stays controlled.
   * Omit to render the range read-only.
   */
  onRangeChange?: (startTime: string, endTime: string) => void;
  anchorX: number;
  anchorY: number;
  officerOptions: OfficerOption[];
  /** Who holds the range now: the picker opens on them, and Clear is offered. */
  currentOfficerId?: string;
  /** Called with what the user chose: an officer for the range, or (when someone holds it) clear. */
  onAction: (action: CellAction) => Promise<void>;
  onCancel: () => void;
  errorMessage?: string;
  /**
   * The counters that can be taken out of service for the range, with `thisCounter` (1-based) the row the
   * range was drawn on. Giving these turns on the Unavailable action, whether the range holds an officer or not.
   */
  unavailable?: {
    thisCounter: number;
    counters: Array<{ num: number; label: string }>;
    /** The counters by zone, for the zone checkboxes. */
    zones: Array<{ label: string; counters: number[] }>;
  };
}

const POPOVER_W = 300;
const POPOVER_H = 520; // tall enough for the Unavailable tab's counter list

export function OfficerAssignPopover({
  startTime,
  endTime,
  shiftType = "morning",
  onRangeChange,
  anchorX,
  anchorY,
  officerOptions,
  currentOfficerId,
  onAction,
  onCancel,
  errorMessage,
  unavailable,
}: OfficerAssignPopoverProps) {
  const selectRef = useRef<HTMLSelectElement>(null);
  const [selectedId, setSelectedId] = useState(currentOfficerId ?? officerOptions[0]?.id ?? NEW_OFFICER_ID);
  // Details for a brand-new officer, asked for right here when the person isn't in the list.
  const [newName, setNewName] = useState("");
  const [newDeployment, setNewDeployment] = useState("");
  const creating = selectedId === NEW_OFFICER_ID;
  const actions: CellActionKind[] = [
    "officer",
    ...(currentOfficerId ? (["clear"] as const) : []),
    ...(unavailable ? (["unavailable"] as const) : []),
  ];
  const [action, setAction] = useState<CellActionKind>(officerOptions.length === 0 && unavailable ? "unavailable" : "officer");
  // Counters to close for the range: this one, and any others picked from the list.
  const [picked, setPicked] = useState<number[]>(unavailable ? [unavailable.thisCounter] : []);
  const labelOf = (n: number) => unavailable?.counters.find((c) => c.num === n)?.label ?? String(n);
  const setMany = (nums: number[], on: boolean) =>
    setPicked((prev) => (on ? [...new Set([...prev, ...nums])] : prev.filter((n) => !nums.includes(n))));
  const closable = picked;
  const [busy, setBusy] = useState(false);

  const isMobile = isNarrow();

  useEffect(() => {
    selectRef.current?.focus();
  }, []);

  const handleConfirm = async () => {
    if (action === "officer" && !selectedId) return;
    if (action === "unavailable" && closable.length === 0) return;
    setBusy(true);
    try {
      await onAction(
        action === "clear" ? { kind: "clear" }
          : action === "unavailable" ? { kind: "unavailable", counters: closable }
            : creating ? { kind: "officer", officerId: NEW_OFFICER_ID, newOfficer: { name: newName.trim(), deployment: newDeployment.trim() } }
              : { kind: "officer", officerId: selectedId },
      );
    } finally {
      setBusy(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleConfirm();
    if (e.key === "Escape") onCancel();
  };

  const color = CELL_ACTION_LOOK[action].color;
  const range = `${fmtHhmm(startTime)} – ${fmtHhmm(endTime)}`;
  const verb = action === "clear" ? "Clear" : action === "unavailable" ? "Unavailable" : creating && action === "officer" ? "New officer" : currentOfficerId ? "Change officer" : "Assign officer";
  const currentLabel = officerOptions.find((o) => o.id === currentOfficerId)?.label ?? currentOfficerId;

  return (
    <PopoverShell anchorX={anchorX} anchorY={anchorY} width={POPOVER_W} approxHeight={POPOVER_H} onCancel={onCancel}>
      <div onKeyDown={handleKey}>
        <PopoverHeader title={`${verb} · ${range}`} color={color} busy={busy} onClose={onCancel} />

        {/* Range pickers — the highlight can be a slot out either side */}
        {onRangeChange && (
          <TimeRangeSelect
            shiftType={shiftType}
            start={startTime}
            end={endTime}
            onChange={onRangeChange}
            isMobile={isMobile}
            accent={action === "officer" ? undefined : color}
            disabled={busy}
          />
        )}

        <ActionTabs actions={actions} value={action} onChange={setAction} isMobile={isMobile} />

        {action === "officer" ? (
          <>
            <label style={{ fontSize: 12, fontWeight: 500, color: "#64748b", display: "block", marginBottom: 6 }}>
              Officer
            </label>
            <select
              ref={selectRef}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{
                width: "100%",
                padding: isMobile ? "12px 14px" : "10px 12px",
                fontSize: isMobile ? 16 : 15,
                fontWeight: 500,
                borderRadius: 8,
                border: "1.5px solid #d1d5db",
                background: "white",
                appearance: "auto",
                marginBottom: 14,
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              {officerOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
              <option value={NEW_OFFICER_ID}>＋ New officer (not in list)…</option>
            </select>
            {creating && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "-6px 0 14px", padding: 10, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: "#0369a1" }}>
                  Adds a new SOS officer for {range} and puts them on this counter.
                </div>
                {([
                  ["Name", newName, setNewName, "e.g. Mr CY"],
                  ["Deployment (optional)", newDeployment, setNewDeployment, "e.g. Ops Rm"],
                ] as const).map(([label, value, set, ph], i) => (
                  <input
                    key={label}
                    aria-label={label}
                    autoFocus={i === 0}
                    value={value}
                    onChange={(e) => set(e.target.value)}
                    placeholder={`${label === "Name" ? "Name" : "Deployment (optional)"} — ${ph}`}
                    style={{ width: "100%", padding: isMobile ? "10px 12px" : "8px 10px", fontSize: isMobile ? 16 : 14, borderRadius: 8, border: "1.5px solid #d1d5db", boxSizing: "border-box" }}
                  />
                ))}
              </div>
            )}
          </>
        ) : action === "unavailable" && unavailable ? (
          <>
            <div style={{ fontSize: 12, fontWeight: 500, color: "#64748b", marginBottom: 6, display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span>Counters out of service for {range}{picked.length > 0 ? ` · ${picked.length} selected` : ""}</span>
              <span style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button type="button" onClick={() => setMany(unavailable.counters.map((c) => c.num), true)} style={linkBtn(color)}>All</button>
                <button type="button" onClick={() => setPicked([])} style={linkBtn(color)}>None</button>
              </span>
            </div>
            <div data-testid="unavailable-counter-picker" style={{ maxHeight: 190, overflowY: "auto", marginBottom: 10, padding: 2 }}>
              {unavailable.zones.map((zone) => {
                const selectable = zone.counters;
                const chosen = selectable.filter((n) => picked.includes(n)).length;
                return (
                  <div key={zone.label} style={{ marginBottom: 8 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 4, cursor: selectable.length ? "pointer" : "default" }}>
                      <input
                        type="checkbox"
                        disabled={selectable.length === 0}
                        checked={selectable.length > 0 && chosen === selectable.length}
                        ref={(el) => { if (el) el.indeterminate = chosen > 0 && chosen < selectable.length; }}
                        onChange={(e) => setMany(zone.counters, e.target.checked)}
                      />
                      {zone.label}
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4 }}>
                      {zone.counters.map((num) => {
                        const on = picked.includes(num);
                        return (
                          <button
                            key={num}
                            type="button"
                            onClick={() => setPicked(on ? picked.filter((n) => n !== num) : [...picked, num])}
                            style={{
                              padding: isMobile ? "9px 0" : "5px 0", fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: "pointer",
                              border: `1.5px solid ${on ? color : "#d1d5db"}`, background: on ? color : "white",
                              color: on ? "white" : "#374151",
                            }}
                          >
                            {labelOf(num)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <ActionNote color={color}>Takes {currentLabel} off this counter for {range}</ActionNote>
        )}

        <PopoverFooter
          action={action}
          busy={busy}
          disabled={(action === "officer" && !selectedId) || (action === "unavailable" && closable.length === 0)}
          error={errorMessage}
          onSave={handleConfirm}
          isMobile={isMobile}
        />
      </div>
    </PopoverShell>
  );
}
